import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../shared/Layout';
import Icon from '../../components/tables/Icon';
import IconGoBack from '../../components/icons/IconGoBack';
import SetModal from './SetModal';
import { getGameType, setPlayUrlPrefix } from '../../variables/gameTypes';
import '../../css/groups.css';
import '../../css/games.css';

// Detail page for a set's member games (route /games/sets/:id) - replaces
// the old set/SetCard.jsx. Filters /api/setcard client-side by set id as a
// safety net: the backend endpoint only reliably filters by `grid`, not
// `set`, so this stays correct even before/without a backend rebuild.
export default function GameSetCard() {
   const { id } = useParams();
   const navigate = useNavigate();
   const [set, setSet] = useState(null);
   const [members, setMembers] = useState([]);
   const [allGames, setAllGames] = useState([]);
   const [modalState, setModalState] = useState('none');
   const [refreshKey, setRefreshKey] = useState(0);
   const [addQuery, setAddQuery] = useState('');
   const [adding, setAdding] = useState(false);

   useEffect(() => {
      axios.get('/api/set/' + id, { withCredentials: true })
         .then(response => setSet(response.data))
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
      axios.get('/api/setcard?set=' + id, { withCredentials: true })
         .then(response => setMembers((response.data || []).filter(elem => elem.set && String(elem.set.id) === String(id))))
         .catch(() => {});
      axios.get('/api/grid', { withCredentials: true })
         .then(response => setAllGames(response.data || []))
         .catch(() => {});
   }, [id, navigate, refreshKey]);

   const bumpRefresh = () => setRefreshKey(prev => prev + 1);
   const stateHundler = (state) => { if(state === 'edit') setModalState('edit'); };
   const closeModal = () => setModalState('none');
   const onSaved = () => { closeModal(); bumpRefresh(); };
   const onDeleted = () => navigate('/games');

   const memberGridIds = useMemo(() => new Set(members.map(m => m.grid && m.grid.id)), [members]);
   const addCandidates = useMemo(() => {
      const query = addQuery.trim().toLowerCase();
      if(!query) return [];
      return allGames
         .filter(g => !memberGridIds.has(g.id))
         .filter(g => (g.name || '').toLowerCase().includes(query))
         .slice(0, 8);
   }, [allGames, memberGridIds, addQuery]);

   const handleAddGame = (gridId) => {
      if(adding) return;
      setAdding(true);
      axios.post('/api/setcard', JSON.stringify({ set: Number(id), grid: gridId }), { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
         .then(() => { setAddQuery(''); setAdding(false); bumpRefresh(); })
         .catch(() => { setAdding(false); window.alert('Не вдалося додати гру до набору'); });
   };

   const handleRemoveMember = (elemId) => {
      axios.delete('/api/setcard/' + elemId, { withCredentials: true })
         .then(() => setMembers(prev => prev.filter(m => m.id !== elemId)))
         .catch(() => window.alert('Не вдалося прибрати гру з набору'));
   };

   if(!set) {
      return (
         <Layout>
            <main className="main-block">
               <div className="main-content txt-c games-page">Завантаження...</div>
            </main>
         </Layout>
      );
   }

   const clearurl = setPlayUrlPrefix();

   return (
      <Layout>
         <main className="main-block">
            <div className="main-content txt-c games-page">
               <div className="game-detail-header">
                  <div className="group-card-back" onClick={() => navigate('/games')} title="До ігор">
                     <IconGoBack />
                  </div>
                  <div className="group-card-title">{set.name}</div>
                  <div className="group-card-header-actions">
                     <Icon mode="edit" id={id} stateHundler={stateHundler} className="icon-edit" classes="icon-dimensions-big" />
                     <Icon mode="copy-url" clearurl={clearurl} item={set} headers={{ urlfield: 'id' }} className="icon-copyurl" classes="icon-dimensions-big" />
                     <Icon mode="copy-iframe" clearurl={clearurl} item={set} className="icon-copyframe" classes="icon-dimensions-big" />
                     <Icon mode="delete" url={'/api/set/' + id} onRefreshHundler={onDeleted} className="icon-delete" classes="icon-dimensions-big" />
                  </div>
               </div>

               <div className="set-add-game-row">
                  <input className="game-cards-input" type="text" value={addQuery}
                     placeholder="Знайти гру, щоб додати до набору..."
                     onChange={e => setAddQuery(e.target.value)} />
                  {addCandidates.length > 0 ? (
                     <div className="set-add-game-dropdown">
                        {addCandidates.map(g => {
                           const gt = getGameType(g.type);
                           return (
                              <div key={g.id} className="set-add-game-option" onClick={() => handleAddGame(g.id)}>
                                 <span className="game-card-type-badge" style={{ '--accent': gt.accent }}>{gt.label}</span>
                                 {g.name}
                              </div>
                           );
                        })}
                     </div>
                  ) : ''}
               </div>

               {members.length === 0 ? (
                  <div className="games-empty">У цьому наборі ще немає ігор — знайдіть гру вище</div>
               ) : (
                  <div className="games-grid">
                     {members.map(elem => {
                        if(!elem.grid) return '';
                        const gt = getGameType(elem.grid.type);
                        return (
                           <div key={elem.id} className="game-card" style={{ '--accent': gt.accent }}>
                              <div className="game-card-body" onClick={() => navigate('/games/' + elem.grid.id)}>
                                 <span className="game-card-type-badge">{gt.label}</span>
                                 <div className="game-card-name">{elem.grid.name}</div>
                              </div>
                              <div className="game-card-actions" onClick={e => e.stopPropagation()}>
                                 <div className="set-member-remove" onClick={() => handleRemoveMember(elem.id)} title="Прибрати з набору">×</div>
                              </div>
                           </div>
                        );
                     })}
                  </div>
               )}
            </div>
         </main>
         <SetModal modalState={modalState} setId={id} onClose={closeModal} onSaved={onSaved} />
      </Layout>
   );
}
